// The app shell: entry -> room -> ending, one RoomClient, the reading language on the document root.
// Everything with a side effect (clock, socket, fetch, microphone, storage, device languages) is injected,
// so the tests drive the real components with fakes.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Clock } from "./clock";
import { deviceDefaults } from "./data/languages";
import { I18nContext, useApplyRoot, useI18nFor, type UiLang } from "./i18n";
import type { MicOpener } from "./media/mic";
import type { KeeperPage } from "./media/micKeeper";
import { primeAudio } from "./media/mic";
import { Publisher, type MediaRoomFactory } from "./media/publish";
import { MicGate } from "./media/micGate";
import { Hearing, type VoiceElement } from "./media/hearing";
import { ReplayPlayer, type AudioLike, type BlobUrls } from "./media/replay";
import { VoiceQueue, type VoiceAudio } from "./media/voiceQueue";
import { micSettingsOf } from "./media/micSettings";
import { VoiceTrackLink } from "./media/voiceTrack";
import {
  LANDING_REFRESH_MS,
  RECORD_DELETED_TOAST_MS,
  RECORD_POLL_IDLE_MAX,
  RECORD_POLL_MS,
  PASSIVE_SPEAK_LANG,
  VOICE_QUEUE_ENABLED,
} from "./constants";
import { readPrefs, remember, type LiveUnder } from "./prefs";
import {
  createPassiveRoom,
  createRoom,
  RoomClient,
  wsUrl,
  type ClientEvent,
  type CreatedRoom,
  type Failure,
  type JoinParams,
  type SocketFactory,
  type SocketObserver,
} from "./protocol/client";
import { IdentityStore, type KeyValue } from "./protocol/identity";
import {
  createLink,
  deleteRecord,
  listLinks,
  listRecords,
  readLinkUrl,
  readRecord,
  recordAuthorization,
  revokeLink,
  searchRecord,
  requestSummary,
  type SummaryAnswer,
  type Fetched,
  type ReadLinkInfo,
  type RecordData,
  type RecordList,
  type RecordSummary,
} from "./protocol/records";
import { anyPending, translatedCount } from "./record/model";
import type { FileSaver } from "./record/summaryFile";
import { listedRooms, mineRooms, type ListedRoom } from "./protocol/rooms";
import { applyMine, noteRoom, readYourRooms, rememberVisit, type YourRoom } from "./room/yourRooms";
import { codeFromHash, displayCode, hostSecretFromHash, normalizeCode, readSecretFromHash } from "./protocol/roomCode";
import type { Knock, ListenMode, SeatKind } from "./protocol/types";
import { emptyRoom, reduce, type RoomAction, type RoomModel } from "./room/model";
import { Ending, LinkDead } from "./ui/Ending";
import { EntryScreen, type EntryChoice } from "./ui/EntryScreen";
import { WaitingScreen } from "./ui/Knocks";
import { MyMajlis } from "./ui/MyMajlis";
import { AdminScreen } from "./ui/AdminScreen";
import { RecordScreen, type RecordStatus } from "./ui/RecordScreen";
import { RoomScreen } from "./ui/RoomScreen";
import { yourRoomName } from "./ui/RoomsList";

export interface AppDeps {
  clock: Clock;
  socket: SocketFactory;
  fetch: typeof fetch;
  openMic: MicOpener;
  /** MAJLIS-71 (D-52): the page's visibility, for the silent re-open of an ended microphone (the document unless a test
   * injects one). */
  page?: KeeperPage;
  /** MAJLIS-9 (A-3): LiveKit rooms for publishing the microphone; tests inject a fake. */
  media: MediaRoomFactory;
  /** MAJLIS-12, UI-C5: the one <audio> element replay plays through (routable; never the mic path). Its canPlayType
   * is the capability probe, asked once. */
  audio: () => AudioLike;
  /** URL.createObjectURL / revokeObjectURL for the fetched recording. */
  blobUrls: BlobUrls;
  /** MAJLIS-62 (D-22): one plain <audio> element per remote voice, in the page - the only path a remote voice plays
   * through (media/hearing.ts says why). */
  voice: () => VoiceElement;
  /** new MediaStream([track]) (jsdom has none). */
  streamOf: (track: MediaStreamTrack) => MediaStream;
  /** iOS/iPadOS or not: picks the reason a browser without WebM/Opus replay is given (detectIOS), and - MAJLIS-64
   * ruling B - whether the live voices are MUTED (iOS ignores element volume) or LOWERED under a spoken translation. */
  isIOS: () => boolean;
  /** MAJLIS-64 (D-43): the ONE persistent <audio> element every spoken translation plays through (never replay's). */
  voiceAudio: () => VoiceAudio;
  /** MAJLIS-64: VOICE_QUEUE_ENABLED unless a test injects it (the safety flag: on since the voice UI shipped). */
  voiceQueueEnabled?: boolean;
  storage: KeyValue | null;
  deviceLangs: readonly string[];
  location: { protocol: string; host: string; hash: string; search?: string };
  setHash: (hash: string) => void;
  wallNow: () => number;
  online: () => boolean;
  reducedMotion: () => boolean;
  /** MAJLIS-109 (UI-C9, D-34): true when the viewport is narrower than LANG_CHIP_COMPACT_BELOW_PX (constants.ts) -
   * the initial read, at mount. */
  chipCompact: () => boolean;
  /** MAJLIS-109 follow-up (consultant ruling, UI-C9): the SAME MediaQueryList `chipCompact` above already reads
   * (main.tsx builds one, not two) - so the chip mode can follow a live width change (a phone rotated, a window
   * resized past the bound) instead of only the width at mount. Optional: a fake deps object that omits it (as
   * every test but the one below does) behaves exactly as before - chipCompact read once, never live. One
   * "change" listener is added while mounted and removed on unmount; this is deliberately NOT a resize listener
   * (resize fires continuously through a drag; change fires only when the query's boolean itself flips). */
  chipCompactQuery?: () => MediaQueryList | undefined;
  /** MAJLIS-153 (UI-C40, consultant ruling 2): matches while the viewport is narrower than RECORD_DAY_SHORT_BELOW_PX
   * (constants.ts) - the record header's day then drops its weekday. Read at mount and followed on "change", as
   * chipCompactQuery. Omitted (a fake deps object): never narrow. */
  dayShortQuery?: () => MediaQueryList | undefined;
  crypto?: Crypto;
  log?: (message: string, body?: unknown) => void;
  /** MAJLIS-116 (T-141, UI-C19): the `?micdiag=1` panel's speech_start hook (micdiag.tsx's MicDiagWiring.speechStart),
   * given only with the flag. Called ONLY for a speech_start of THIS seat (the same filter below already applies to
   * seat_updated); undefined otherwise, so nothing is added without the flag. */
  observeSpeechStart?: () => void;
  /** MAJLIS-116 (T-141): the `?micdiag=1` panel's socket observer (micdiag.tsx's MicDiagWiring.socketObserver),
   * threaded straight into RoomClientOptions.observer; undefined without the flag. */
  socketObserver?: SocketObserver;
  /** MAJLIS-140 (P-39, ruling d): the browser's user-agent string, read on the phone only to name the device class and
   * browser family of a passive seat's mic settings (never sent). Default: navigator.userAgent. */
  userAgent?: () => string;
  /** MAJLIS-154 (UI-C41): the summary's "Save as file" - the system share sheet where it takes files, otherwise a
   * download. Default: the browser's own (record/summaryFile.ts browserSaver). */
  saveFile?: FileSaver;
}

// MAJLIS-37: "waiting" - this join is held at a locked room's door (a knock), before any room.
// MAJLIS-67: "records" is My majlis (the list), "record" one finished majlis (UI-S3). MAJLIS-66: "linkdead" is the one
// screen for a read link that no longer opens - turned off, deleted, or never real (mock-up 5d).
type Phase = "entry" | "waiting" | "room" | "ending" | "records" | "record" | "linkdead" | "admin";

export function App({ deps }: { deps: AppDeps }) {
  // D-33: what this device remembers WINS over the device's language list, field by field. The phone is
  // consulted only where nothing is stored - that precedence is this line and the three useStates below it,
  // and entry.test.tsx holds both sides of it (a stored choice survives a reload; cleared storage falls
  // back to the device).
  const stored = useMemo(() => readPrefs(deps.storage), [deps.storage]);
  const defaults = useMemo(() => deviceDefaults(deps.deviceLangs), [deps.deviceLangs]);
  const identity = useMemo(() => new IdentityStore(deps.storage, deps.crypto), [deps.storage, deps.crypto]);
  const phaseRef = useRef<Phase>("entry");
  const client = useMemo(
    () =>
      new RoomClient({
        url: wsUrl(deps.location),
        socket: deps.socket,
        clock: deps.clock,
        identity,
        log: deps.log,
        observer: deps.socketObserver, // MAJLIS-116 (T-141): undefined without the flag
      }),
    [deps, identity],
  );

  // A-3: this seat's microphone to LiveKit (joined/resumed grants, mute, leave, pagehide); when LiveKit gives the
  // connection up while the socket stays live, it asks for a fresh grant itself (media_grant, backoff on deps.clock)
  // EM-25: the one owner of the microphone's enabled flag. His mute and the replay gate both go through it, and
  // the publisher re-asserts it after livekit-client writes that flag itself (mute/unmute).
  const micGate = useMemo(() => new MicGate(deps.clock), [deps]);

  // MAJLIS-62 (D-22): the other seats' voices, each through its own plain <audio> element. Ruling 2: while one waits
  // for a tap (its play() was refused), the room shows "Tap to hear the room".
  const [tapToHear, setTapToHear] = useState(false);
  const hearing = useMemo(
    () =>
      new Hearing({
        createElement: deps.voice,
        streamOf: deps.streamOf,
        log: deps.log,
        onWaitingChange: setTapToHear,
        isIOS: deps.isIOS,
      }),
    [deps],
  );

  // MAJLIS-140 (D-59, P-39): a passive seat's voice track - subscribed only while wanted; a subscription that fails or
  // never comes shows the switch's line and is reported (condition 5)
  const [playbackUnavailable, setPlaybackUnavailable] = useState(false);
  const voiceTrack = useMemo(
    () =>
      new VoiceTrackLink({
        clock: deps.clock,
        log: deps.log,
        onFailed: (reason) => {
          setPlaybackUnavailable(true);
          client.voiceTrackFailed(reason);
        },
      }),
    [deps, client],
  );

  const publisher = useMemo(
    () =>
      new Publisher({
        factory: deps.media,
        log: deps.log,
        clock: deps.clock,
        requestGrant: () => client.requestMediaGrant(),
        reassertMic: () => micGate.reapply(),
        hearing,
        voiceTrack,
      }),
    [deps, client, micGate, hearing, voiceTrack],
  );

  // UI-C5, T-304: ONE replay player for the canvas; GET /api/audio/{segment_id} with this seat's token in the header -
  // MAJLIS-67 (P-23): or, while a finished majlis's record is open, with the record's credential instead
  const replay = useMemo(
    () =>
      new ReplayPlayer({
        fetch: deps.fetch,
        seatToken: () => client.seatToken(),
        audio: deps.audio,
        urls: deps.blobUrls,
        clock: deps.clock,
        isIOS: deps.isIOS,
        log: deps.log,
        gate: micGate,
      }),
    [deps, client, micGate],
  );

  const [model, setModel] = useState<RoomModel>(emptyRoom);
  const apply = useCallback((a: RoomAction) => setModel((m) => reduce(m, a)), []);

  // MAJLIS-64 (D-43, D-08, EM-25): the phone's voice queue - interpreter mode's spoken translations, pulled one at a
  // time at the head, through ONE element, holding the microphone gate while each plays. Auto-play is behind
  // VOICE_QUEUE_ENABLED; a refused play() joins the existing "Tap to hear the room" line.
  const [voiceWaiting, setVoiceWaiting] = useState(false);
  const voiceQueue = useMemo(
    () =>
      new VoiceQueue({
        fetch: deps.fetch,
        seatToken: () => client.seatToken(),
        audio: deps.voiceAudio,
        clock: deps.clock,
        enabled: deps.voiceQueueEnabled ?? VOICE_QUEUE_ENABLED,
        gate: micGate,
        stopOthers: () => replay.stop(),
        onCapped: () => apply({ type: "voice_capped" }),
        onWaitingChange: setVoiceWaiting,
        log: deps.log,
        track: voiceTrack, // MAJLIS-140: a passive seat's clips play on its voice track, never an MP3 element
        onTrackFailed: (reason, segmentId, phone) => {
          setPlaybackUnavailable(true);
          if (phone && reason === "no_start") client.voiceTrackFailed(reason, segmentId);
        },
        onTrackPlaying: (segmentId, ms) => client.voicePlayed(segmentId, ms), // MAJLIS-144 (T-166): the phone's number
      }),
    [deps, client, micGate, replay, apply, voiceTrack],
  );

  // MAJLIS-ADMIN (UI-C37): the unlinked #/admin opens the owner's read-only admin view instead of the entry
  const [phase, setPhase] = useState<Phase>(() => (deps.location.hash.startsWith("#/admin") ? "admin" : "entry"));
  // MAJLIS-42 (D-33, the approved mock-up's 1b-1e): "I speak" is NEVER taken from the phone. Only what this phone
  // remembers (the person's own earlier choice, at entry or in a room) fills it; otherwise it starts empty and the
  // entry screen asks for it. "I read in" keeps its device default (ruling 9: the interface is drawn in it, UI-P3).
  const [speak, setSpeak] = useState(stored.speak ?? "");
  // whether the value shown is one this phone REMEMBERED (from storage, or confirmed in a room), not one just picked
  // on this screen: the entry screen then says "Remembered on this phone" in place of the first-visit hint (1d)
  const [speakRemembered, setSpeakRemembered] = useState(stored.speak !== null);
  const [readLang, setReadLang] = useState(stored.read ?? defaults.read);
  const [seatKind, setSeatKind] = useState<SeatKind>(stored.seatKind ?? "personal");
  const [linkCode, setLinkCode] = useState(() => codeFromHash(deps.location.hash));
  // MAJLIS-135 (T-161, consultant ruling 2): the join link's code that ARRIVED (a hashchange) after the last join
  // started - null when none did, or when the last one to arrive carried no code. "Back to the start" keeps this one
  // and clears only a code that belonged to the room just left.
  const freshCode = useRef<string | null>(null);
  // MAJLIS-80 (D-15, UI-C14, EM-18): a page opened by a host link (#/h/SECRET) joins by that secret. Read once, held in
  // memory only (never stored, never logged, never in a URL the server sees), and cleared from the address bar below.
  const [hostSecret, setHostSecret] = useState(() => hostSecretFromHash(deps.location.hash));
  // MAJLIS-80 ruling (b): the room code the server gave this seat as a HOST (null for a guest), so a host who opened
  // the host link can show and share the join link. Memory only.
  const [hostCode, setHostCode] = useState<string | null>(null);
  // the secret of the join in flight, for the socket's event handler (which is subscribed once)
  const hostSecretRef = useRef<string | null>(null);
  const [initialName, setInitialName] = useState(stored.name);
  const [busy, setBusy] = useState<"join" | "create" | null>(null);
  const [entryFailure, setEntryFailure] = useState<Failure | "offline" | null>(null);
  const [endFailure, setEndFailure] = useState<Failure | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  // MAJLIS-62, ruling 6 (DEPLOY CONDITION until the two-phone echo acceptance T-118 passes): every seat joins not
  // listening; from the join on, this is the server's record only.
  const [listening, setListening] = useState(false);
  // MAJLIS-137 (UI-C39): a passive room's "Auto-play translations" switch - this phone's, for this join only, and OFF at
  // every join (the owner: "default off"). Never sent to the server: it only decides whether the queue auto-plays.
  const [autoPlay, setAutoPlay] = useState(false);
  // MAJLIS-63 (UI-C17): joined/resumed say whether this seat is the room's host (the only one shown the mode switch).
  const [host, setHost] = useState(false);
  // MAJLIS-44 (P-17b): each speak_lang_refused from the server; RoomScreen shows the picker's refusal line on a new one.
  const [speakRefusals, setSpeakRefusals] = useState(0);
  // MAJLIS-44: the speaking language the server last recorded for THIS seat (joined/resumed, then seat_updated), so a
  // CHANGE of it - and only that - is remembered on the phone (ruling 2: majlis.speak, this phone only).
  const seatSpeakRef = useRef<string | null>(null);
  const [joined, setJoined] = useState<JoinParams | null>(null);
  const [created, setCreated] = useState<CreatedRoom | null>(null);
  const [offline, setOffline] = useState(() => !deps.online());
  const [reduced] = useState(() => deps.reducedMotion());
  const [chipCompact, setChipCompact] = useState(() => deps.chipCompact());
  const [dayShort, setDayShort] = useState(() => deps.dayShortQuery?.()?.matches ?? false);
  // MAJLIS-64 (UI-C21, D-43(2)): the live voices under a spoken phrase - lowered or muted - a personal setting kept on
  // this phone (prefs.ts); iOS mutes whatever it says (Hearing), and the room does not offer it there.
  const [liveUnder, setLiveUnder] = useState<LiveUnder>(stored.liveUnder);
  const [ios] = useState(() => deps.isIOS());
  // MAJLIS-37 (P-25, P-26): this knocker's "a host is here" (the waiting screen's away line), and - for a HOST - the
  // knocks waiting at the door, exactly as the server last listed them (never edited here).
  const [hostPresent, setHostPresent] = useState(true);
  const [knocks, setKnocks] = useState<Knock[]>([]);
  // MAJLIS-67 (UI-S3, P-20, P-21): this identity's finished majlis (null: not asked or not answered yet), the record
  // open now, and whether its translations are still on their way (the record is re-read while they are).
  const [records, setRecords] = useState<RecordSummary[] | null>(null);
  const [recordsFailed, setRecordsFailed] = useState(false);
  const [recordRoom, setRecordRoom] = useState<string | null>(null);
  const [record, setRecord] = useState<RecordData | null>(null);
  const [recordStatus, setRecordStatus] = useState<RecordStatus>("loading");
  const [recordWaiting, setRecordWaiting] = useState(false);
  const [recordReload, setRecordReload] = useState(0);
  // MAJLIS-66 (P-33, UI-S3): the records this device opened by a still-live read link ("Opened here with a read link")
  const [readRecords, setReadRecords] = useState<RecordSummary[]>([]);
  // MAJLIS-66 (P-30, UI-C15): the open record's read links for the host's sheets (null: not loaded), the address of a
  // link made in the share sheet - held in memory only until that sheet closes (EM-4: shown once) - and the toast
  // on the list after a delete (4b)
  const [links, setLinks] = useState<ReadLinkInfo[] | null>(null);
  const [freshLink, setFreshLink] = useState<string | null>(null);
  const [deletedToast, setDeletedToast] = useState(false);
  // MAJLIS-66 (P-33, EM-18): a page opened by a read link (#/v/SECRET). The secret is read from the fragment ONCE,
  // the fragment leaves the address bar at once (below, as the host link's does), and the secret is sent ONE time, in
  // the Authorization header, to name the record and record this identity as its reader (DM-12); it is then dropped.
  // It is held in memory only while that one request is owed (or being retried after a network failure) - never in
  // storage, never in a URL, never in a log.
  const [readProbe, setReadProbe] = useState<string | null>(() => readSecretFromHash(deps.location.hash));
  const [readProbeTick, setReadProbeTick] = useState(0);
  const recordAccess = useRef<"host" | "read">("host");
  // MAJLIS-126 (D-55, UI-C35): Your rooms - this phone's own memory, never the code - and the listed rooms (null: not
  // answered yet), and the id of the room this seat is in (joined.room_id), for Your rooms and the header.
  // Your rooms lives in the phone's storage; this counter says it changed (a join, the server's answer), and the list
  // is read again from storage whenever the landing shows
  const [yoursVersion, setYoursVersion] = useState(0);
  const [listed, setListed] = useState<ListedRoom[] | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);

  const go = useCallback((p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  // D-33: a choice a person makes is remembered on this device at the moment they make it - the entry
  // screen's two dropdowns and the seat-kind row, and the room's reading-language chip. A default that was
  // merely offered is never written, so "nothing is stored" keeps meaning "this person never chose".
  const chooseSpeak = useCallback((tag: string) => {
    setSpeak(tag);
    setSpeakRemembered(false);
    remember(deps.storage, "speak", tag);
  }, [deps.storage]);
  const chooseRead = useCallback((tag: string) => {
    setReadLang(tag);
    remember(deps.storage, "read", tag);
  }, [deps.storage]);
  const chooseSeatKind = useCallback((kind: SeatKind) => {
    setSeatKind(kind);
    remember(deps.storage, "seatKind", kind);
  }, [deps.storage]);

  // MAJLIS-67: the list (P-20). Asked only by a device that already HOLDS an identity (peek never mints one), so a
  // first visit asks the server nothing and its start screen is today's (mock-up 1b).
  const loadRecords = useCallback(async () => {
    const me = identity.peek();
    if (!me) {
      setRecords([]);
      return [] as RecordSummary[];
    }
    setRecordsFailed(false);
    const res = await listRecords(deps.fetch, { identity: me });
    if (res.ok) {
      setRecords(res.value.records);
      setReadRecords(res.value.read ?? []);
      return res.value.records;
    }
    // every refusal is the one 404 - an identity the server has never seen hosts nothing
    if (res.why === "refused") setRecords([]);
    else setRecordsFailed(true);
    return null;
  }, [deps.fetch, identity]);

  const openRecord = useCallback(
    (roomId: string) => {
      replay.reset();
      replay.setAuthorization(recordAuthorization({ identity: identity.get() })); // P-23: Replay in the record
      setRecord(null);
      setRecordStatus("loading");
      setRecordRoom(roomId);
      setLinks(null);
      setFreshLink(null);
      go("record");
    },
    [go, replay, identity],
  );

  // MAJLIS-164 (ruling 1): the interface follows "I read in"; a lazy catalogue still loading leaves the last one drawn
  const onCatalogueError = useCallback(
    (lang: UiLang, err: unknown) => deps.log?.("interface catalogue did not load; the last one drawn stays", { lang, err: String(err) }),
    [deps],
  );
  const i18n = useI18nFor(readLang, undefined, onCatalogueError);

  // UI-P3: lang and dir on the root follow the reading language - once its catalogue is drawn (MAJLIS-164, whole or hold)
  useApplyRoot(document, readLang, i18n.ready);

  // MAJLIS-109 follow-up (consultant ruling, UI-C9): the chip mode follows the SAME MediaQueryList live - ONE
  // "change" listener, added while mounted and removed on unmount; never a resize listener (a resize fires on
  // every pixel of a drag, "change" only when the query's boolean itself flips). Omitted deps.chipCompactQuery
  // (every deps object but the test below) leaves chipCompact exactly as it was: read once, at mount.
  useEffect(() => {
    const mql = deps.chipCompactQuery?.();
    if (!mql) return;
    const onChange = () => setChipCompact(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [deps]);
  // MAJLIS-153 (ruling 2): the record header's narrow day follows its query live, the same way
  useEffect(() => {
    const mql = deps.dayShortQuery?.();
    if (!mql) return;
    const onChange = () => setDayShort(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [deps]);

  // MAJLIS-80 (decision, flagged): the host link leaves the address bar as soon as it is read, so a screenshot, a
  // shared tab or the history does not carry it. replaceState: no navigation, no hashchange, nothing sent.
  const hostLinkOpened = useRef(hostSecret !== null || readProbe !== null); // MAJLIS-66: a read link's too
  useEffect(() => {
    if (hostLinkOpened.current) deps.setHash("");
  }, [deps]);

  // MAJLIS-67 (ruling 9, mock-up 1b): the start screen asks for this identity's finished majlis each time it shows, so
  // the "My majlis" row appears after the host's first room has ended.
  useEffect(() => {
    const me = phase === "entry" ? identity.peek() : null;
    if (!me) return;
    let alive = true;
    void listRecords(deps.fetch, { identity: me }).then((res) => {
      if (alive && res.ok) {
        setRecords(res.value.records);
        setReadRecords(res.value.read ?? []);
      }
    });
    return () => {
      alive = false;
    };
  }, [phase, identity, deps.fetch]);

  // MAJLIS-67 (ruling 9): a host link opened AFTER its room closed opens the room's RECORD. Asked once, at the start, with
  // the link's secret in the Authorization header (P-23; never a URL): a CLOSED room's link makes this identity its host
  // (DM-10) and names it in `opened`. An OPEN room's link opens nothing here, and the entry screen joins it as before.
  const hostLinkProbe = useRef(hostSecret);
  useEffect(() => {
    // asked ONCE (the ref is spent at once), and never cancelled: StrictMode's second effect run must not drop it
    const secret = hostLinkProbe.current;
    if (!secret) return;
    hostLinkProbe.current = null;
    void listRecords(deps.fetch, { identity: identity.get(), hostSecret: secret }).then((res) => {
      if (!res.ok || !res.value.opened || phaseRef.current !== "entry") return;
      setRecords(res.value.records);
      setHostSecret(null); // spent: the identity is the record's host now, without the link
      openRecord(res.value.opened);
    });
  }, [deps.fetch, identity, openRecord]);

  // MAJLIS-66 (P-33, mock-up 5b/5d): a read link opens its record straight away, with no entry form. The one request
  // carries the secret in the header; the server names the room (`opened`) and remembers this identity as its reader,
  // so every later request - the record, search, Replay - goes with the identity alone. A link that no longer opens
  // (turned off, deleted, never real: the server's one 404) is the one "can't be opened" screen; a network failure
  // keeps the secret in memory for "Try again".
  const probeSent = useRef(-1);
  useEffect(() => {
    // sent ONCE per link (or per "Try again"), and never cancelled: StrictMode's second effect run must not send twice
    if (readProbe === null || probeSent.current === readProbeTick) return;
    probeSent.current = readProbeTick;
    setRecordStatus("loading");
    setRecord(null);
    setRecordRoom(null);
    go("record");
    const secret = readProbe;
    void listRecords(deps.fetch, { identity: identity.get(), readSecret: secret }).then((res: Fetched<RecordList>) => {
      if (phaseRef.current !== "record") return; // the person left meanwhile
      if (res.ok && res.value.opened) {
        setReadProbe(null); // spent
        setRecords(res.value.records);
        setReadRecords(res.value.read ?? []);
        openRecord(res.value.opened);
      } else if (!res.ok && res.why === "failed") {
        setRecordStatus("failed"); // "Try again" sends it once more; the secret is still only in memory
      } else {
        setReadProbe(null);
        go("linkdead");
      }
    });
  }, [readProbe, readProbeTick, deps.fetch, identity, openRecord, go]);

  // MAJLIS-66 (P-30, P-31, P-32): the host's sheets. Every call is host-only on the server; a reader never sees them.
  const loadLinks = useCallback(async () => {
    if (recordRoom === null) return;
    const res = await listLinks(deps.fetch, { identity: identity.get() }, recordRoom);
    if (res.ok) setLinks(res.value.links);
  }, [deps.fetch, identity, recordRoom]);
  const makeLink = useCallback(
    async (label: string | null) => {
      if (recordRoom === null) return false;
      const res = await createLink(deps.fetch, { identity: identity.get() }, recordRoom, label);
      if (!res.ok) return false;
      setFreshLink(readLinkUrl(`${deps.location.protocol}//${deps.location.host}`, res.value.secret));
      await loadLinks();
      return true;
    },
    [deps, identity, recordRoom, loadLinks],
  );
  const turnOff = useCallback(
    async (id: string) => {
      if (recordRoom === null) return false;
      const res = await revokeLink(deps.fetch, { identity: identity.get() }, recordRoom, id);
      if (res.ok) await loadLinks();
      return res.ok;
    },
    [deps.fetch, identity, recordRoom, loadLinks],
  );
  const toastTimer = useRef<number | null>(null);
  const removeRecord = useCallback(async () => {
    if (recordRoom === null) return false;
    const res = await deleteRecord(deps.fetch, { identity: identity.get() }, recordRoom);
    if (!res.ok) return false; // never optimistic (4b): the record stays open and the confirmation says so
    replay.reset();
    replay.setAuthorization(null);
    setRecordRoom(null);
    setRecord(null);
    setLinks(null);
    setFreshLink(null);
    await loadRecords();
    go("records");
    setDeletedToast(true);
    if (toastTimer.current !== null) deps.clock.clearTimeout(toastTimer.current);
    toastTimer.current = deps.clock.setTimeout(() => setDeletedToast(false), RECORD_DELETED_TOAST_MS);
    return true;
  }, [deps, identity, recordRoom, replay, loadRecords, go]);

  // MAJLIS-67 (P-21, D-45): the open record, in the reading language; read again every RECORD_POLL_MS while
  // translations are on their way, until RECORD_POLL_IDLE_MAX reads in a row bring none (then the rest stand in the
  // original, as a failed translation does live). A new language or room starts afresh.
  useEffect(() => {
    if (phase !== "record" || recordRoom === null) return;
    let alive = true;
    let timer: number | null = null;
    let idle = 0;
    let seen = -1;
    const cred = { identity: identity.get() };
    const once = async () => {
      const res = await readRecord(deps.fetch, cred, recordRoom, readLang);
      if (!alive) return;
      if (!res.ok) {
        setRecordWaiting(false);
        // MAJLIS-66 (5d): a READER whose link was turned off (or whose record was deleted) meanwhile is told so
        if (res.why === "refused" && recordAccess.current === "read") {
          recordAccess.current = "host";
          replay.reset();
          replay.setAuthorization(null);
          setRecordRoom(null);
          setRecord(null);
          go("linkdead");
          return;
        }
        setRecordStatus("failed");
        if (res.why === "refused") setRecord(null);
        return;
      }
      setRecord(res.value);
      recordAccess.current = res.value.access === "read" ? "read" : "host";
      setRecordStatus("ready");
      const n = translatedCount(res.value);
      idle = n > seen ? 0 : idle + 1;
      seen = n;
      if (anyPending(res.value) && idle < RECORD_POLL_IDLE_MAX) {
        setRecordWaiting(true);
        timer = deps.clock.setTimeout(() => void once(), RECORD_POLL_MS);
      } else {
        setRecordWaiting(false);
      }
    };
    void once();
    return () => {
      alive = false;
      if (timer !== null) deps.clock.clearTimeout(timer);
    };
  }, [phase, recordRoom, readLang, recordReload, deps.fetch, deps.clock, identity, replay, go]);

  // MAJLIS-126 (UI-C35, P-35): the landing asks for the listed rooms, and about THIS phone's own rooms, when it is shown
  // and every LANDING_REFRESH_MS while it stays shown (ASSUMPTION) - never while in a room. /mine is asked only by a
  // phone that holds an identity AND remembers a room; the list is the public one. A failure keeps what was shown.
  useEffect(() => {
    if (phase !== "entry" || hostSecret !== null) return;
    let alive = true;
    const refresh = () => {
      void listedRooms(deps.fetch).then((res) => {
        if (alive && res.ok && Array.isArray(res.value.rooms)) setListed(res.value.rooms);
        else if (alive) setListed((l) => l ?? []);
      });
      const me = identity.peek();
      const ids = readYourRooms(deps.storage, deps.wallNow()).map((r) => r.room_id);
      if (!me || ids.length === 0) return;
      void mineRooms(deps.fetch, me, ids).then((res) => {
        if (!alive || !res.ok || !Array.isArray(res.value.rooms)) return;
        applyMine(deps.storage, deps.wallNow(), res.value.rooms);
        setYoursVersion((n) => n + 1);
      });
    };
    refresh();
    const timer = deps.clock.setInterval(refresh, LANDING_REFRESH_MS);
    return () => {
      alive = false;
      deps.clock.clearInterval(timer);
    };
  }, [phase, hostSecret, deps, identity]);

  // MAJLIS-126 (ruling 11): inside the room, its name and listing handle as the server gives them now go to Your rooms,
  // so a room of yours that is listed shows once - there - and not again in Listed rooms.
  useEffect(() => {
    if (phase !== "room" || roomId === null) return;
    noteRoom(deps.storage, deps.wallNow(), roomId, { name: model.roomName, handle: model.handle, passive: model.passive });
  }, [phase, roomId, model.roomName, model.handle, model.passive, deps]);
  // read from storage when the landing shows (or it changed): the phone's own memory is the one source
  const yours = useMemo<YourRoom[]>(
    () => (phase === "entry" || phase === "room" ? readYourRooms(deps.storage, deps.wallNow()) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yoursVersion: storage changed; phase: the landing shows
    [deps, phase, yoursVersion],
  );

  // MAJLIS-153 (P-40): one summary POST for the open record (the screen repeats it after each Retry-After)
  const summariseRecord = useCallback(
    async (lang: string): Promise<SummaryAnswer> => {
      if (recordRoom === null) return { kind: "failed" };
      return requestSummary(deps.fetch, { identity: identity.get() }, recordRoom, lang);
    },
    [deps.fetch, identity, recordRoom],
  );

  // P-22: the words in the body; a failure is "no match" (the reader can type again)
  const searchOpenRecord = useCallback(
    async (q: string) => {
      if (recordRoom === null) return null;
      const res = await searchRecord(deps.fetch, { identity: identity.get() }, recordRoom, readLang, q);
      return res.ok ? res.value : null;
    },
    [deps.fetch, identity, recordRoom, readLang],
  );

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const hash = () => {
      // MAJLIS-135 (T-161, consultant ruling 1): a link opened while the tab sits on the ending screen is handled
      // exactly as in a fresh tab - the finished room's socket is let go first (as "Back to the start" does), and a
      // join or host link then shows the ENTRY screen, prefilled. Nothing is sent: the person still taps Join.
      const ending = phaseRef.current === "ending";
      const read = readSecretFromHash(window.location.hash);
      if (read) {
        // MAJLIS-66: a read link pasted into this tab - the same as opening it; it leaves the address bar at once
        // (the record effect moves the tab to the record, from the ending screen too)
        if (ending) client.dispose();
        deps.setHash("");
        setReadProbe(read);
        setReadProbeTick((n) => n + 1);
        return;
      }
      const secret = hostSecretFromHash(window.location.hash);
      if (secret) {
        // a host link pasted into this tab: the same as opening it, and it leaves the address bar at once
        setHostSecret(secret);
        setLinkCode(null);
        freshCode.current = null;
        deps.setHash("");
        if (ending) leaveEnding();
        return;
      }
      const code = codeFromHash(window.location.hash);
      if (code) setHostSecret(null); // a join link arrived instead: it wins, as a later link always has
      setLinkCode(code);
      freshCode.current = code;
      if (ending && code) leaveEnding();
    };
    // the ending screen's way out for a link: what "Back to the start" does, minus clearing what just arrived
    const leaveEnding = () => {
      client.dispose();
      setCreated(null);
      setHostCode(null);
      go("entry");
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("hashchange", hash);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("hashchange", hash);
    };
  }, [deps, client, go]);

  // UI-C9 (MAJLIS-49): in the room the choice is also the SEAT's - the server translates for it from now on, re-sends
  // what it already holds in that language and backfills the visible window - and every translation re-renders.
  const chooseReadInRoom = useCallback(
    (tag: string) => {
      chooseRead(tag);
      client.setReadLang(tag);
      apply({ type: "read_lang", now: deps.clock.now() });
    },
    [chooseRead, client, apply, deps.clock],
  );

  useEffect(
    () =>
      client.subscribe((e: ClientEvent) => {
        switch (e.kind) {
          case "knock": // MAJLIS-37 (P-26): held at the door, or let in (joined and room_state follow)
            if (e.state === "waiting") {
              setHostPresent(e.hostPresent);
              setBusy(null);
              go("waiting");
            }
            return;
          case "knocks": // MAJLIS-37 (P-25): a host's list of who waits
            setKnocks(e.knocks);
            return;
          case "seat":
            setKnocks([]); // a new seat context: a host's list, if any waits, follows its room_state
            apply({ type: "me", seat: e.seat, listenMode: e.listenMode });
            voiceQueue.resync(); // a resume replayed the missed phrases before this: they are history, never voiced
            setMuted(e.seat.muted);
            setListening(e.seat.listening); // MAJLIS-62: OUR record's Listen (ruling 6: off on a new seat)
            setHost(e.host);
            seatSpeakRef.current = e.seat.speak_lang;
            if (e.reply === "joined" && e.roomId) {
              // MAJLIS-126 (D-55, P-36): Your rooms keeps this room by its id and role - never by its code
              const id = e.roomId;
              setRoomId(id);
              rememberVisit(deps.storage, deps.wallNow(), {
                room_id: id,
                role: e.host ? "host" : "joined",
                host: e.host ? e.seat.name : null,
              });
              setYoursVersion((n) => n + 1);
            }
            if (e.roomCode) {
              // (the client gives a code to a HOST only: RoomClient.learnCode is the one gate, tested on its own)
              // MAJLIS-80 ruling (b): a host knows the code from here on. A seat that came in by the host link now
              // names the room by it (Rejoin), no longer needs the secret (the server holds the claim), and - like a
              // room just created - puts the join link in the address bar so a reload joins again (decision, flagged).
              const code = e.roomCode;
              setHostCode(code);
              setJoined((j) => (j && (j.room !== code || j.hostSecret) ? { ...j, room: code, hostSecret: undefined } : j));
              if (hostSecretRef.current !== null) {
                hostSecretRef.current = null;
                setHostSecret(null);
                deps.setHash(`#/r/${code}`);
                setLinkCode(code);
              }
            }
            return;
          case "media":
            publisher.grant(e.grant, e.reply, e.ref ?? null);
            return;
          case "media_rate_limited":
            publisher.grantRefused(e.ref);
            return;
          case "speak_lang_refused": // MAJLIS-44: the seat keeps its language; the speak picker says so
            setSpeakRefusals((n) => n + 1);
            return;
          case "room_state":
            apply({ type: "room_state", data: e.data });
            return;
          case "earlier":
            // P-19 (MAJLIS-89): the join's earlier phrases. History: the voice queue never learns of them (it queues
            // only phrases it saw land live), so none is voiced unless a person taps Voice (L-7).
            apply({ type: "earlier", phrases: e.phrases, now: deps.clock.now(), wall: deps.wallNow() });
            return;
          case "engine":
            apply({ type: "engine", state: e.state });
            return;
          case "translation": // P-10 (MAJLIS-49)
            apply({ type: "translation", data: e.data });
            voiceQueue.onTranslation(e.data); // MAJLIS-64: interpreter mode queues it if every rule holds
            return;
          case "compare_mode": // MAJLIS-113 (P-34): this phone is the compared seat's; nothing here reaches the voice queue
            apply({ type: "compare_mode", since: e.since });
            return;
          case "compare_phrase":
            apply({ type: "compare_phrase", data: e.data });
            return;
          case "voice_track": // MAJLIS-140 (P-39): a clip on this passive seat's voice track
            if (e.data.state === "playing") setPlaybackUnavailable(false);
            voiceQueue.onVoiceTrack(e.data);
            return;
          case "event": {
            const ev = e.event;
            apply({ type: "event", event: ev, now: deps.clock.now(), wall: deps.wallNow() });
            voiceQueue.onEvent(ev);
            // MAJLIS-116 (T-141, UI-C19): the micdiag observation hook, the same "our own seat" filter as below.
            if (ev.type === "speech_start" && ev.data.seat === client.seat?.id) deps.observeSpeechStart?.();
            if (ev.type === "seat_updated" && ev.data.seat.id === client.seat?.id) {
              setMuted(ev.data.seat.muted);
              setListening(ev.data.seat.listening);
              const spoken = ev.data.seat.speak_lang;
              if (seatSpeakRef.current !== spoken) {
                // MAJLIS-44 (D-34, ruling 2): the SERVER's confirmed change is this phone's choice from now on - the
                // entry screen opens on it next time (the mock-up's 1d), and a Rejoin joins with it.
                seatSpeakRef.current = spoken;
                chooseSpeak(spoken);
                setSpeakRemembered(true);
                setJoined((j) => (j && j.speak_lang !== spoken ? { ...j, speak_lang: spoken } : j));
              }
            }
            return;
          }
          case "status":
            if (e.status === "live") {
              setBusy(null);
              setReconnecting(false);
              go("room");
            } else if (e.status === "reconnecting") {
              setReconnecting(true);
            } else if (e.status === "left") {
              publisher.stop(); // a person who left is not still streaming audio to our server
              replay.reset();
              voiceQueue.reset();
              setBusy(null);
              setReconnecting(false);
              setEndFailure(null);
              go("ending");
            } else if (e.status === "failed") {
              publisher.stop(); // the seat is gone (room closed, replaced, ...): nothing left to publish into
              replay.reset();
              voiceQueue.reset();
              setBusy(null);
              setReconnecting(false);
              // MAJLIS-37: a knock that ended (denied, timed out) has its own screen - even a denied identity's
              // second try, which is refused before it ever waits
              const knockEnd = e.failure === "denied" || e.failure === "knock_timeout";
              if (phaseRef.current === "entry" && !knockEnd) {
                setEntryFailure(e.failure ?? "server_error");
                // MAJLIS-80: a host link that names no open room (or whose person was removed) is spent: the entry
                // goes back to its ordinary form, with the reason shown. A passing failure keeps it, to try again.
                const f = e.failure;
                if (f === "room_not_found" || f === "room_closed" || f === "removed") {
                  hostSecretRef.current = null;
                  setHostSecret(null);
                }
              } else {
                setEndFailure(e.failure ?? "server_error");
                go("ending");
              }
            }
            return;
        }
      }),
    [client, apply, deps, go, publisher, replay, voiceQueue, chooseSpeak],
  );

  useEffect(() => () => client.dispose(), [client]);
  useEffect(() => () => replay.reset(), [replay]);
  useEffect(() => () => voiceQueue.reset(), [voiceQueue]);

  // the page going away disconnects from LiveKit (and so does unmounting)
  useEffect(() => {
    const hide = () => publisher.stop();
    window.addEventListener("pagehide", hide);
    return () => {
      window.removeEventListener("pagehide", hide);
      publisher.stop();
    };
  }, [publisher]);

  // MUTE keeps the track published and mutes it; UNMUTE resumes the same track
  useEffect(() => publisher.setMuted(muted), [publisher, muted]);

  // MAJLIS-62: this phone plays the other voices only while it listens (the server also stops sending them)...
  // MAJLIS-63: and not at all while the room is screen-only - a nicety on top of the server's enforcement, exactly as
  // for Listen off (the SERVER sends this phone no audio in a screen-only room; T-114 counts that, not this).
  const liveAudio = model.audioMode === "live";
  useEffect(() => hearing.setListening(listening && liveAudio), [hearing, listening, liveAudio]);
  // ...and D-38: while THIS phone replays a phrase (clip plus tail), the live voices are silent here only. The
  // silence IS the microphone gate's replay state, so it ends on every path the gate ends on. MAJLIS-64: while a
  // spoken translation holds the gate instead, the live voices are LOWERED (muted on iOS, ruling B), and a replay
  // pre-empts the voice queue (proposal 4.6) - all three follow the one gate.
  useEffect(
    () =>
      micGate.watchKinds((k) => {
        hearing.setSilenced(k.replay);
        hearing.setLowered(k.voice);
        voiceQueue.setReplaying(k.replay);
      }),
    [micGate, hearing, voiceQueue],
  );

  useEffect(() => hearing.setMuteUnder(liveUnder === "muted"), [hearing, liveUnder]);
  const chooseLiveUnder = useCallback(
    (v: LiveUnder) => {
      setLiveUnder(v);
      remember(deps.storage, "liveUnder", v);
    },
    [deps.storage],
  );
  // UI-C21: the Listen choice goes to the server (set_listen_mode, recorded on the seat, no reply) and to the model at
  // once; the next joined/resumed carries the server's record back.
  const chooseListenMode = useCallback(
    (mode: ListenMode) => {
      client.setListenMode(mode);
      apply({ type: "listen_mode", mode });
    },
    [client, apply],
  );

  // MAJLIS-140 (P-39, ruling d): a PASSIVE seat's microphone settings, ONCE per seat - the four getSettings() booleans,
  // the device class and the browser family (the UA string never leaves the phone). Sent when both the seat and the
  // microphone's track are known, whichever comes last.
  const micTrack = useRef<MediaStreamTrack | null>(null);
  const micReported = useRef<string | null>(null);
  const reportMic = useCallback(() => {
    const seat = model.mySeatId;
    const track = micTrack.current;
    if (!model.passive || !seat || !track || micReported.current === seat) return;
    micReported.current = seat;
    const ua = deps.userAgent?.() ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
    client.micSettings(micSettingsOf(track, ua, deps.isIOS()));
  }, [client, deps, model.mySeatId, model.passive]);
  useEffect(reportMic, [reportMic]);

  // MAJLIS-64: the queue auto-plays only in interpreter mode (listen_mode translated), in a live-audio room, with
  // Listen on and voicing on; any change away from that empties it at once (proposal 4.9).
  useEffect(
    () =>
      voiceQueue.configure({
        mySeatId: model.mySeatId,
        readLang,
        listenMode: model.listenMode,
        liveAudio,
        listening,
        voiceOn: model.voiceOn,
        passive: model.passive, // MAJLIS-137
        autoPlay,
      }),
    [voiceQueue, model.mySeatId, readLang, model.listenMode, liveAudio, listening, model.voiceOn, model.passive, autoPlay],
  );
  // iOS plays audio only after a tap (NOT MEASURED ON iOS): a voice the browser refused to start is tried again
  // from inside the next tap anywhere on the page.
  useEffect(() => {
    const unlock = () => {
      hearing.unlock();
      voiceQueue.unlock(); // MAJLIS-64: the voice element is unlocked from a tap too (the Listen tap is one)
    };
    document.addEventListener("click", unlock, true);
    document.addEventListener("touchend", unlock, true);
    return () => {
      document.removeEventListener("click", unlock, true);
      document.removeEventListener("touchend", unlock, true);
    };
  }, [hearing, voiceQueue]);

  const startJoin = useCallback(
    (params: JoinParams) => {
      replay.reset();
      replay.setAuthorization(null); // MAJLIS-67: a room's Replay is the seat's, never a record's credential
      voiceQueue.reset();
      setModel(emptyRoom());
      setJoined(params);
      setMuted(false);
      setAutoPlay(false); // MAJLIS-137: off at every join
      setPlaybackUnavailable(false); // MAJLIS-140: a new join starts with no failure line
      setHost(false);
      setHostCode(null);
      setRoomId(null);
      setKnocks([]);
      setHostPresent(true);
      hostSecretRef.current = params.hostSecret ?? null;
      freshCode.current = null; // a code is fresh only if it arrives after this join
      setListening(params.seat_kind !== "shared"); // EM-6: a table phone sits beside the speakers
      setBusy("join");
      client.join(params);
    },
    [client, replay, voiceQueue],
  );

  const onSubmit = async (c: EntryChoice) => {
    primeAudio();
    setEntryFailure(null);
    if (c.passive) {
      // MAJLIS-137 (P-38, D-57): a passive room - created, then joined by its host link's secret (the answer carries
      // nothing else: no code, no join link, so nothing goes in the address bar). Only the reading language is
      // remembered: the name was not asked, and the seat's language and kind are the room's, never this phone's.
      remember(deps.storage, "read", c.read);
      setBusy("create");
      const res = await createPassiveRoom(deps.fetch, identity);
      if (!res.ok) {
        setBusy(null);
        setEntryFailure(res.failure);
        return;
      }
      const link = res.room.host_link;
      const secret = hostSecretFromHash(link.slice(link.indexOf("#")));
      if (secret === null) {
        setBusy(null);
        setEntryFailure("server_error");
        return;
      }
      setCreated(null);
      startJoin({
        room: null,
        hostSecret: secret,
        name: c.name,
        speak_lang: PASSIVE_SPEAK_LANG,
        read_lang: c.read,
        seat_kind: "shared",
      });
      return;
    }
    // The name has always been remembered here; D-33 remembers the rest of the choice beside it, so a
    // reload cannot hand this seat back to the phone's language list.
    remember(deps.storage, "name", c.name);
    remember(deps.storage, "speak", c.speak);
    remember(deps.storage, "read", c.read);
    remember(deps.storage, "seatKind", c.seatKind);
    if (c.listing) {
      // MAJLIS-126 (P-36): a room chosen in Listed rooms, by its handle - with the typed code when it is keyed
      setCreated(null);
      const room = c.code === null ? null : displayCode(normalizeCode(c.code)!);
      startJoin({ room, listing: c.listing, name: c.name, speak_lang: c.speak, read_lang: c.read, seat_kind: c.seatKind });
      return;
    }
    if (hostSecret !== null) {
      // MAJLIS-80 (D-15): opened by the host link - join by its secret, naming no room (the link carries no code)
      setCreated(null);
      startJoin({ room: null, hostSecret, name: c.name, speak_lang: c.speak, read_lang: c.read, seat_kind: c.seatKind });
      return;
    }
    let code: string;
    if (c.code === null) {
      setBusy("create");
      const res = await createRoom(deps.fetch, identity);
      if (!res.ok) {
        setBusy(null);
        setEntryFailure(res.failure);
        return;
      }
      setCreated(res.room);
      code = res.room.code;
      deps.setHash(`#/r/${code}`);
      setLinkCode(code);
    } else {
      setCreated(null);
      code = displayCode(normalizeCode(c.code)!);
    }
    startJoin({ room: code, name: c.name, speak_lang: c.speak, read_lang: c.read, seat_kind: c.seatKind });
  };

  // MAJLIS-126 (P-36, ruling 9): one tap on a Your rooms row joins that room by its id, with the remembered choices
  const onRejoin = (id: string, c: Omit<EntryChoice, "code">) => {
    primeAudio();
    setEntryFailure(null);
    remember(deps.storage, "name", c.name);
    remember(deps.storage, "speak", c.speak);
    remember(deps.storage, "read", c.read);
    remember(deps.storage, "seatKind", c.seatKind);
    setCreated(null);
    startJoin({ room: null, rejoin: id, name: c.name, speak_lang: c.speak, read_lang: c.read, seat_kind: c.seatKind });
  };
  // ruling 11: a room of yours that is listed appears once, in Your rooms; a row the phone cannot name yet waits for
  // the server's answer
  const ownHandles = new Set(yours.map((r) => r.handle).filter((x): x is string => x !== null));
  const landingListed = listed === null ? null : listed.filter((r) => !ownHandles.has(r.handle));
  const landingYours = yours.filter((r) => r.name !== null || r.host !== null);
  // MAJLIS-126 (ruling 6): a seat that came in without the code (a Listed open room, or Your rooms) shows the room's
  // NAME where the code sits
  const here = roomId === null ? undefined : yours.find((r) => r.room_id === roomId);
  const roomTitle = model.roomName ?? (here && (here.name !== null || here.host !== null) ? yourRoomName(i18n.t, here) : "");

  return (
    <I18nContext.Provider value={i18n}>
      {phase === "entry" && (
        <EntryScreen
          hostLink={hostSecret !== null}
          linkCode={linkCode}
          initialName={initialName}
          speak={speak}
          speakRemembered={speakRemembered}
          read={readLang}
          seatKind={seatKind}
          onSpeak={chooseSpeak}
          onRead={chooseRead}
          onSeatKind={chooseSeatKind}
          busy={busy}
          failure={entryFailure ?? (offline ? "offline" : null)}
          onSubmit={(c) => void onSubmit(c)}
          records={hostSecret === null ? (records?.length ?? 0) + readRecords.length : 0}
          onMyMajlis={() => go("records")}
          yours={landingYours}
          listed={landingListed}
          onRejoin={onRejoin}
          now={deps.wallNow()}
        />
      )}
      {phase === "records" && (
        <MyMajlis
          records={records}
          read={readRecords}
          deletedToast={deletedToast}
          failed={recordsFailed}
          onOpen={openRecord}
          onBack={() => go("entry")}
          onRetry={() => void loadRecords()}
        />
      )}
      {phase === "admin" && (
        <AdminScreen
          fetchImpl={deps.fetch}
          replay={replay}
          clock={deps.clock}
          reduced={reduced}
          readLang={readLang}
          onReadLang={chooseRead}
          dayShort={dayShort}
          chipCompact={chipCompact}
          wallNow={deps.wallNow}
          host={deps.location.host}
        />
      )}
      {phase === "record" && (
        <RecordScreen
          record={record}
          status={recordStatus}
          waiting={recordWaiting}
          readLang={readLang}
          onReadLang={chooseRead}
          replay={replay}
          reduced={reduced}
          clock={deps.clock}
          onBack={() => {
            replay.reset();
            replay.setAuthorization(null); // back to the seat token for the next room
            setRecordRoom(null);
            setRecord(null);
            setLinks(null);
            setFreshLink(null);
            setReadProbe(null);
            void loadRecords();
            go("records");
          }}
          onRetry={() => {
            setRecordStatus("loading");
            if (readProbe !== null) setReadProbeTick((n) => n + 1); // the read link's one request, once more
            else setRecordReload((n) => n + 1);
          }}
          onSearch={searchOpenRecord}
          links={links}
          fresh={freshLink}
          onLoadLinks={() => void loadLinks()}
          onCreateLink={makeLink}
          onRevokeLink={turnOff}
          onDropFresh={() => setFreshLink(null)}
          onDelete={removeRecord}
          onSummary={summariseRecord}
          dayShort={dayShort}
          chipCompact={chipCompact}
          saver={deps.saveFile}
        />
      )}
      {phase === "linkdead" && (
        <LinkDead
          onStartAgain={() => {
            go("entry");
          }}
        />
      )}
      {phase === "waiting" && joined && (
        <WaitingScreen
          code={joined.room ?? ""}
          name={joined.name}
          speakLang={joined.speak_lang}
          hostPresent={hostPresent}
          onCancel={() => {
            // MAJLIS-37 (2a): Cancel withdraws the knock at once - the socket closes and the server drops it - and
            // returns to the entry form with the fields kept
            client.dispose();
            setBusy(null);
            setInitialName(joined.name);
            if (joined.room) setLinkCode(joined.room);
            go("entry");
          }}
        />
      )}
      {phase === "room" && joined && (
        <RoomScreen
          model={model}
          code={joined.room ?? hostCode ?? ""}
          title={roomTitle}
          onListing={(listing, name) => client.setListing(listing, name)}
          reconnecting={reconnecting}
          offline={offline}
          readLang={readLang}
          onReadLang={chooseReadInRoom}
          muted={muted}
          onMute={() => {
            if (model.passive) {
              // MAJLIS-137 (owner rule): in a passive room the mic is never the floor - Mute mutes, and a playing
              // translation plays on (the queue holds no gate there)
              if (muted) client.unmute();
              else client.mute();
              setMuted(!muted);
              return;
            }
            // MAJLIS-64 (D-43(4), UI-C24): the mic tap gives him the floor - it stops a spoken translation at once and
            // drops the queue's backlog. While a voice is sounding the tap opens the mic (it does not mute him).
            if (!muted && voiceQueue.sounding()) return voiceQueue.micTap();
            if (muted) {
              client.unmute();
              voiceQueue.micTap();
            } else client.mute();
            setMuted(!muted);
          }}
          listening={listening}
          autoPlay={autoPlay}
          playbackUnavailable={playbackUnavailable}
          onAutoPlay={() => setAutoPlay((on) => !on)}
          onListen={() => {
            // UI-C10 -> P-17: the server records it and enforces it, and seat_updated is the ONLY thing that changes
            // what this shows. Not optimistic: a tap inside ruling 4's interval is refused quietly (no seat_updated),
            // and an optimistic flip would then show - and play - the opposite of what the server enforces.
            client.setListen(!listening);
          }}
          tapToHear={tapToHear || voiceWaiting}
          host={host}
          onAudioMode={(mode) => client.setAudioMode(mode)}
          hostJoinUrl={hostCode ? `${deps.location.protocol}//${deps.location.host}/#/r/${hostCode}` : null}
          // MAJLIS-163 (UI-C14): every seat's invite row - the created room's link, else one built from the code held
          joinUrl={
            created?.join_url ??
            ((joined.room ?? hostCode) ? `${deps.location.protocol}//${deps.location.host}/#/r/${joined.room ?? hostCode}` : null)
          }
          startedAt={here ? (here.created ?? here.first) : null}
          onEndMajlis={() => client.closeRoom()}
          onRemoveSeat={(seatId) => client.removeSeat(seatId)}
          onNameVoice={(voiceId, name) => client.nameVoice(voiceId, name)}
          onSpeakLang={(tag) => client.setSpeakLang(tag)}
          speakRefusals={speakRefusals}
          onLock={(locked) => client.setLock(locked)}
          knocks={host ? knocks : []}
          onAnswerKnock={(identityId, admit) => client.answerKnock(identityId, admit)}
          onLeave={() => {
            publisher.stop();
            replay.reset();
            voiceQueue.reset();
            micGate.reset();
            client.leave();
          }}
          clock={deps.clock}
          openMic={deps.openMic}
          page={deps.page}
          onMicTrack={(track) => {
            publisher.setTrack(track);
            micTrack.current = track;
            reportMic();
          }}
          onMicHold={(want, tap) => {
            // MAJLIS-71 (D-52): the EXISTING P-3 messages, never a new one. The microphone's track ended while he was
            // unmuted: the seat reads muted (the server enforces it as any mute, so the others see "muted", D-42); a
            // re-open brought it back: unmuted again. The mic tap's re-open is a mic tap (D-43(4)): it takes the floor.
            if (want !== muted) {
              if (want) client.mute();
              else client.unmute();
            }
            if (!want && tap) voiceQueue.micTap();
            setMuted(want);
          }}
          reduced={reduced}
          chipCompact={chipCompact}
          created={created}
          replay={replay}
          micGate={micGate}
          voice={voiceQueue}
          onListenMode={chooseListenMode}
          liveUnder={liveUnder}
          onLiveUnder={chooseLiveUnder}
          ios={ios}
        />
      )}
      {phase === "ending" && (
        <Ending
          key={endFailure ?? "left"}
          failure={endFailure}
          host={host}
          onOpenRecord={() => {
            // MAJLIS-67 (ruling 9, mock-up 1a): the room this host has just ended is the most recently CLOSED of the rooms
            // it hosts - a seated host is told room_closed only by End majlis, never by the idle close. The wire gives
            // the phone no room id (P-1), so the list is asked; if it cannot say, the list itself is shown.
            client.dispose();
            void loadRecords().then((list) => {
              const latest = (list ?? []).reduce<RecordSummary | null>(
                (a, r) => (a === null || Date.parse(r.closed) > Date.parse(a.closed) ? r : a),
                null,
              );
              if (latest) openRecord(latest.room_id);
              else go("records");
            });
          }}
          onRejoin={() => joined && startJoin(joined)}
          onStartAgain={() => {
            client.dispose();
            setCreated(null);
            setHostSecret(null);
            // MAJLIS-135 (T-161, consultant ruling 2): a join link opened AFTER the join (while in the room) stays -
            // in the address bar and on the entry screen; only a code that belonged to the room just left is cleared
            const left = joined?.room ?? hostCode;
            const fresh = freshCode.current;
            freshCode.current = null;
            setHostCode(null);
            if (fresh !== null && fresh !== left) {
              setLinkCode(fresh);
            } else {
              deps.setHash("");
              setLinkCode(null);
            }
            go("entry");
          }}
        />
      )}
    </I18nContext.Provider>
  );
}
